/**
 * Canonical subscription pricing for the client.
 *
 * THE SINGLE SOURCE OF TRUTH FOR TIER PRICES IN THE CLIENT BUNDLE. No page,
 * modal, or dropdown label may hardcode a tier price -- import from here.
 *
 * This mirrors server/config/pricing.js, which derives its numbers from
 * siteConfig.TIERS. The client cannot require() server code (CRA will not
 * compile imports from outside src/), so the values are restated here and the
 * mirror is kept honest two ways:
 *
 *   1. `GET /api/pricing/tiers` serves the server's canonical table, and
 *      `fetchTierPricing()` below overlays it at runtime. Anything rendered
 *      through `useTierPricing()` therefore shows the server's numbers even if
 *      this file goes stale.
 *   2. The server logs a loud warning at boot if the two tables disagree.
 *
 * The constants below are the offline fallback used before the fetch resolves
 * and if it fails -- never a second opinion about what the price is.
 *
 * Authoritative reference: deedstack.com/pricing, confirmed against the real
 * $69.00 Starter charge to D & D Lozano Investments LLC on 2026-08-12.
 */

export const TIER_KEYS = ['free_trial', 'starter', 'pro', 'enterprise'];

export const TIER_PRICING = {
  free_trial: { key: 'free_trial', name: 'Free Trial', monthlyPrice: 0, setupFee: 0, trialDays: 14 },
  starter: { key: 'starter', name: 'Starter', monthlyPrice: 69, setupFee: 0, trialDays: 0 },
  pro: { key: 'pro', name: 'Pro', monthlyPrice: 99, setupFee: 299, trialDays: 0 },
  enterprise: { key: 'enterprise', name: 'Enterprise', monthlyPrice: 199, setupFee: 999, trialDays: 0 }
};

/**
 * Look up a tier. Unknown keys return null rather than a $0 default, so a
 * typo surfaces as a missing label instead of a confident wrong price.
 */
export function getTierPricing(tier) {
  if (!tier) return null;
  return TIER_PRICING[String(tier).toLowerCase()] || null;
}

/** Monthly price in dollars; 0 for unknown tiers. */
export function monthlyPriceFor(tier) {
  return getTierPricing(tier)?.monthlyPrice ?? 0;
}

/** Setup fee in dollars; 0 for unknown tiers. */
export function setupFeeFor(tier) {
  return getTierPricing(tier)?.setupFee ?? 0;
}

/** Whole dollars render without cents: "$69", not "$69.00". */
export function formatPrice(amount) {
  const value = Number(amount) || 0;
  return Number.isInteger(value)
    ? `$${value.toLocaleString()}`
    : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Dropdown/label text for a tier, e.g. "Starter ($69/mo)".
 * Unknown tiers fall back to the raw key so the UI shows what it was given
 * rather than inventing a price for it.
 */
export function tierLabel(tier) {
  const pricing = getTierPricing(tier);
  if (!pricing) return String(tier || 'Unknown');
  if (pricing.monthlyPrice === 0) {
    return pricing.trialDays
      ? `${pricing.name} (${pricing.trialDays} days)`
      : pricing.name;
  }
  return `${pricing.name} (${formatPrice(pricing.monthlyPrice)}/mo)`;
}

/** "$299 setup + $99/mo", or just "$69/mo" when there is no setup fee. */
export function tierPriceSummary(tier) {
  const pricing = getTierPricing(tier);
  if (!pricing) return '';
  const monthly = pricing.monthlyPrice > 0 ? `${formatPrice(pricing.monthlyPrice)}/mo` : '';
  if (pricing.setupFee > 0) {
    return monthly
      ? `${formatPrice(pricing.setupFee)} setup + ${monthly}`
      : `${formatPrice(pricing.setupFee)} setup`;
  }
  return monthly || 'Free';
}

/**
 * Fetch the server's canonical pricing table and overlay it on TIER_PRICING.
 *
 * Mutates the shared table on success so every consumer -- including modules
 * that read TIER_PRICING directly -- picks up the authoritative numbers.
 * Failures are swallowed on purpose: stale-but-present pricing beats a page
 * that renders no price at all.
 */
export async function fetchTierPricing(apiBase = '') {
  try {
    const response = await fetch(`${apiBase}/api/pricing/tiers`);
    if (!response.ok) return TIER_PRICING;

    const body = await response.json();
    const tiers = Array.isArray(body?.tiers) ? body.tiers : [];

    tiers.forEach(tier => {
      if (tier?.key && TIER_PRICING[tier.key]) {
        TIER_PRICING[tier.key] = { ...TIER_PRICING[tier.key], ...tier };
      }
    });
  } catch {
    // Offline or endpoint unavailable -- keep the compiled-in fallback.
  }
  return TIER_PRICING;
}

const pricing = {
  TIER_KEYS,
  TIER_PRICING,
  getTierPricing,
  monthlyPriceFor,
  setupFeeFor,
  formatPrice,
  tierLabel,
  tierPriceSummary,
  fetchTierPricing
};

export default pricing;
